/**
 * Settings Service for V2 Services
 */

import { FirestoreService } from '../db/FirestoreService';
import { Settings, AppSettings, BookingSettings, PricingSettings, NotificationSettings, RobotSettings } from '../models/Settings.model';

export class SettingsService {
  private firestore: FirestoreService;
  private readonly COLLECTION = 'settings';
  private readonly SETTINGS_DOC_ID = 'global';

  constructor() {
    this.firestore = FirestoreService.getInstance();
  }

  async getSettings(): Promise<Settings> {
    const doc = await this.firestore.getDocument(this.COLLECTION, this.SETTINGS_DOC_ID);
    return doc ? Settings.fromFirestore(doc) : new Settings();
  }

  async updateAppSettings(updates: Partial<AppSettings>, updatedBy: string): Promise<void> {
    const settings = await this.getSettings();
    settings.updateAppSettings(updates, updatedBy);
    await this.firestore.setDocument(this.COLLECTION, this.SETTINGS_DOC_ID, settings.toFirestore());
  }

  async updateBookingSettings(updates: Partial<BookingSettings>, updatedBy: string): Promise<void> {
    const settings = await this.getSettings();
    settings.updateBookingSettings(updates, updatedBy);
    await this.firestore.setDocument(this.COLLECTION, this.SETTINGS_DOC_ID, settings.toFirestore());
  }

  async updatePricingSettings(updates: Partial<PricingSettings>, updatedBy: string): Promise<void> {
    const settings = await this.getSettings();
    settings.updatePricingSettings(updates, updatedBy);
    await this.firestore.setDocument(this.COLLECTION, this.SETTINGS_DOC_ID, settings.toFirestore());
  }

  async updateNotificationSettings(updates: Partial<NotificationSettings>, updatedBy: string): Promise<void> {
    const settings = await this.getSettings();
    settings.updateNotificationSettings(updates, updatedBy);
    await this.firestore.setDocument(this.COLLECTION, this.SETTINGS_DOC_ID, settings.toFirestore());
  }

  async updateRobotSettings(updates: Partial<RobotSettings>, updatedBy: string): Promise<void> {
    const settings = await this.getSettings();
    settings.updateRobotSettings(updates, updatedBy);
    await this.firestore.setDocument(this.COLLECTION, this.SETTINGS_DOC_ID, settings.toFirestore());
  }

  async enableMaintenance(message?: string, updatedBy?: string): Promise<void> {
    const settings = await this.getSettings();
    settings.enableMaintenance(message, updatedBy);
    await this.firestore.setDocument(this.COLLECTION, this.SETTINGS_DOC_ID, settings.toFirestore());
  }

  async disableMaintenance(updatedBy?: string): Promise<void> {
    const settings = await this.getSettings();
    settings.disableMaintenance(updatedBy);
    await this.firestore.setDocument(this.COLLECTION, this.SETTINGS_DOC_ID, settings.toFirestore());
  }

  async isMaintenanceMode(): Promise<boolean> {
    const settings = await this.getSettings();
    return settings.app.maintenance;
  }

  async getMaintenanceMessage(): Promise<string | undefined> {
    const settings = await this.getSettings();
    return settings.app.maintenanceMessage;
  }

  async calculateBookingPrice(squareFeet: number, isRushHour: boolean = false, isWeekend: boolean = false, isHoliday: boolean = false): Promise<number> {
    const settings = await this.getSettings();
    const { pricing } = settings;

    let price = pricing.basePrice + (squareFeet * pricing.pricePerSquareFoot);

    if (price < pricing.minimumCharge) {
      price = pricing.minimumCharge;
    }

    if (isRushHour) {
      price *= pricing.rushHourMultiplier;
    }

    if (isWeekend) {
      price *= pricing.weekendMultiplier;
    }

    if (isHoliday) {
      price *= pricing.holidayMultiplier;
    }

    const tax = price * pricing.taxRate;
    return Math.round((price + tax) * 100) / 100;
  }

  async getDiscountForUser(totalBookings: number): Promise<number> {
    const settings = await this.getSettings();
    const { discountThresholds } = settings.pricing;

    const thresholds = Object.entries(discountThresholds)
      .map(([key, discount]) => ({
        bookings: parseInt(key.split('_')[0]),
        discount
      }))
      .sort((a, b) => b.bookings - a.bookings);

    for (const threshold of thresholds) {
      if (totalBookings >= threshold.bookings) {
        return threshold.discount;
      }
    }

    return 0;
  }

  async isBookingAllowed(requestedDate: Date): Promise<{ allowed: boolean; reason?: string }> {
    const settings = await this.getSettings();
    const { booking } = settings;

    const now = new Date();
    const timeDiff = requestedDate.getTime() - now.getTime();
    const hoursDiff = timeDiff / (1000 * 60 * 60);
    const daysDiff = timeDiff / (1000 * 60 * 60 * 24);

    if (hoursDiff < booking.minAdvanceBookingHours) {
      return {
        allowed: false,
        reason: `Bookings must be made at least ${booking.minAdvanceBookingHours} hours in advance`
      };
    }

    if (daysDiff > booking.maxAdvanceBookingDays) {
      return {
        allowed: false,
        reason: `Bookings cannot be made more than ${booking.maxAdvanceBookingDays} days in advance`
      };
    }

    const isToday = now.toDateString() === requestedDate.toDateString();
    if (isToday && !booking.allowSameDayBooking) {
      return {
        allowed: false,
        reason: 'Same-day bookings are not allowed'
      };
    }

    return { allowed: true };
  }

  async canCancelBooking(bookingDate: Date): Promise<{ allowed: boolean; reason?: string }> {
    const settings = await this.getSettings();
    const { booking } = settings;

    const now = new Date();
    const timeDiff = bookingDate.getTime() - now.getTime();
    const hoursDiff = timeDiff / (1000 * 60 * 60);

    if (hoursDiff < booking.cancellationWindowHours) {
      return {
        allowed: false,
        reason: `Bookings must be cancelled at least ${booking.cancellationWindowHours} hours before the scheduled time`
      };
    }

    return { allowed: true };
  }

  async getSupportedLanguages(): Promise<string[]> {
    const settings = await this.getSettings();
    return settings.app.supportedLanguages;
  }

  async getSupportedCurrencies(): Promise<string[]> {
    const settings = await this.getSettings();
    return settings.app.supportedCurrencies;
  }

  async getDefaultCurrency(): Promise<string> {
    const settings = await this.getSettings();
    return settings.app.defaultCurrency;
  }

  async getTimeZone(): Promise<string> {
    const settings = await this.getSettings();
    return settings.app.timeZone;
  }

  async getRobotCleaningModes(): Promise<string[]> {
    const settings = await this.getSettings();
    return settings.robots.availableCleaningModes;
  }

  async getDefaultCleaningMode(): Promise<string> {
    const settings = await this.getSettings();
    return settings.robots.defaultCleaningMode;
  }

  async resetToDefaults(updatedBy: string): Promise<void> {
    const defaultSettings = new Settings();
    defaultSettings.lastUpdatedBy = updatedBy;
    defaultSettings.lastUpdatedAt = new Date();
    await this.firestore.setDocument(this.COLLECTION, this.SETTINGS_DOC_ID, defaultSettings.toFirestore());
  }

  async exportSettings(): Promise<string> {
    const settings = await this.getSettings();
    return JSON.stringify(settings.toFirestore(), null, 2);
  }

  async importSettings(settingsJson: string, updatedBy: string): Promise<void> {
    try {
      const settingsData = JSON.parse(settingsJson);
      settingsData.lastUpdatedBy = updatedBy;
      settingsData.lastUpdatedAt = new Date();

      const settings = Settings.fromFirestore(settingsData);
      await this.firestore.setDocument(this.COLLECTION, this.SETTINGS_DOC_ID, settings.toFirestore());
    } catch (error) {
      throw new Error('Invalid settings format');
    }
  }

  subscribeToSettings(callback: (settings: Settings) => void): () => void {
    return this.firestore.subscribeToDocument(
      this.COLLECTION,
      this.SETTINGS_DOC_ID,
      (doc) => {
        const settings = doc ? Settings.fromFirestore(doc) : new Settings();
        callback(settings);
      }
    );
  }
}